/**
 * TitleScene — 吸引页（attract mode）。
 * 满座六营的弹珠静物 + 缓慢环绕的相机 + 偶发的"萤火"微光，
 * 让第一眼就是一间暖光棋室，而非一个加载界面。
 */
import * as THREE from 'three';
import { CAMP_POSITIONS } from '@shared/constants/camps';
import { PLAYER_COLORS } from '@shared/constants/board';
import { MARBLE_RADIUS } from '@shared/constants/board';
import type { ThemePreset } from '@shared/config/theme';
import type { HexPos } from '@shared/types/game';
import { hexKey } from '@shared/utils/hexMath';
import {
  NICKNAME_MAX_LEN,
  ONLINE_PLAYER_COUNTS,
  PARTY_CODE_LENGTH,
  SLOW_TIP_MS,
  SOFT_TIP_MS,
} from '@shared/net/constants';
import type { AppContext, Scene } from './context';
import { buildBoard, type BoardHandle } from '../core/BoardBuilder';
import { createMarble, type MarbleMesh } from '../core/MarbleFactory';
import { $settings, $theme, updateSettings } from '../store/settingsStore';
import { $net, patchNet, setNetPlayerCount, type NetState } from '../store/netStore';
import { netClient } from '../net/netClient';
import { APP_VERSION, netLogger } from '../debug/logger';
import { netErrorKey, tr, trf, type DictKey } from '../ui/i18n';
import { openTeamSetup } from '../ui/teamSetupPanel';
import { partyInvite, plainInvite, share } from '../share/share';

/** Cycle of the "firefly" shimmer (seconds). */
const FIREFLY_PERIOD = 2.2;

export class TitleScene implements Scene {
  private ctx!: AppContext;
  private board!: BoardHandle;
  private marbles: MarbleMesh[] = [];
  private overlay: HTMLElement | null = null;
  private firefly: MarbleMesh | null = null;
  private fireflyStart = 0;
  private unsubs: (() => void)[] = [];

  // ── 联机面板（modal-veil 承载，$net 状态机驱动渲染） ──
  private netVeil: HTMLElement | null = null;
  private netTick: ReturnType<typeof setInterval> | null = null;
  private netPanelUnsub: (() => void) | null = null;
  /** 主体渲染指纹：无关字段（RTT / 时钟偏移）变化不重渲染，保住输入焦点。 */
  private netBodySig = '';

  enter(ctx: AppContext): void {
    this.ctx = ctx;
    this.buildStage($theme.get());

    // Attract camera: slow damped drift around the parlour table.
    const rig = ctx.engine.getRig();
    rig.view(Math.PI * 0.72, 0.9, 38);
    rig.setAutoRotate(0.045);

    ctx.hud.setTurn(null);
    ctx.hud.setChain(null);
    ctx.hud.setPlayers([]);
    ctx.hud.setHint(null);

    this.buildOverlay();
    this.bindSettings();
  }

  update(dt: number, elapsed: number): void {
    this.board.update(dt, elapsed);
    // 萤火：一颗弹珠短暂苏醒，随即归于沉静（克制律）。
    const k = (elapsed - this.fireflyStart) / FIREFLY_PERIOD;
    if (k >= 1) {
      this.firefly?.setEmissive(0);
      const list = this.marbles;
      this.firefly = list[Math.floor(Math.random() * list.length)] ?? null;
      this.fireflyStart = elapsed;
    } else if (this.firefly) {
      this.firefly.setEmissive(Math.sin(k * Math.PI) * 0.32);
    }
  }

  onThemeChange(theme: ThemePreset): void {
    this.buildStage(theme);
  }

  exit(): void {
    this.closeOnline();
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.clearStage();
    this.overlay?.remove();
    this.overlay = null;
    this.ctx.engine.getRig().setAutoRotate(0);
  }

  // ── Stage ───────────────────────────────────────────────
  private buildStage(theme: ThemePreset): void {
    this.clearStage();
    const scene = this.ctx.engine.getScene();

    this.board = buildBoard(theme);
    scene.add(this.board.group);
    this.ctx.engine.registerHoles(this.board.holeWorld, this.board.feltY);

    const iridescence = $settings.get().marbleIridescence;
    for (let camp = 0; camp < 6; camp++) {
      for (const pos of CAMP_POSITIONS[camp] ?? []) {
        this.spawnMarble(camp, pos, iridescence, scene);
      }
    }
    this.ctx.engine.registerPickables(this.marbles.map((m) => m.mesh));
    this.firefly = null;
  }

  private spawnMarble(seat: number, pos: HexPos, iridescence: number, scene: THREE.Scene): void {
    const key = hexKey(pos);
    const hole = this.board.holeWorld.get(key);
    if (!hole) return;
    const m = createMarble({
      seat,
      pos,
      key,
      color: PLAYER_COLORS[seat] ?? '#E63946',
      iridescence,
    });
    m.mesh.position.set(hole.x, hole.y + MARBLE_RADIUS, hole.z);
    scene.add(m.mesh);
    this.marbles.push(m);
  }

  private clearStage(): void {
    const scene = this.ctx.engine.getScene();
    for (const m of this.marbles) {
      scene.remove(m.mesh);
      m.material.dispose();
    }
    this.marbles = [];
    if (this.board) {
      scene.remove(this.board.group);
      this.board.dispose();
    }
  }

  // ── Overlay ─────────────────────────────────────────────
  private buildOverlay(): void {
    const root = document.createElement('div');
    root.className = 'overlay overlay-title';
    this.overlay = root;

    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);

    // 半透明黑底遮罩（对齐 SCREENS.md §2.1），提升文字可读性。
    const scrim = document.createElement('div');
    scrim.className = 'title-scrim';

    const card = document.createElement('div');
    card.className = 'menu-card';

    // v2.1（SCREENS §2.1/§2.3）：装饰线 → 标题 → 拉丁副行 → 副标题 → 卡片，120ms 交错入场。
    const ornament = document.createElement('div');
    ornament.className = 'title-ornament';
    ornament.textContent = '✦';

    const title = document.createElement('h1');
    title.className = 'title-word';
    title.textContent = t('title');

    // 拉丁大写副行（§2.2）：英文独立成行，两种语言永不争同一行。
    const latin = document.createElement('div');
    latin.className = 'title-latin';
    latin.textContent = t('titleLatin');

    const sub = document.createElement('p');
    sub.className = 'subtitle';
    sub.textContent = t('subtitle');

    const rule = document.createElement('div');
    rule.className = 'rule-line';

    const tagline = document.createElement('p');
    tagline.className = 'tagline';
    tagline.textContent = t('tagline');

    // 主菜单：本地开局 / 联机随机匹配（v0.5 先布局，功能渐进）。
    const menu = document.createElement('div');
    menu.className = 'menu-group';

    const localCard = document.createElement('button');
    localCard.type = 'button';
    localCard.className = 'menu-item menu-item--primary';
    const localName = document.createElement('span');
    localName.className = 'mi-name';
    localName.textContent = t('start');
    const localDesc = document.createElement('span');
    localDesc.className = 'mi-desc';
    // v4.0（SCREENS §14.2）：副行如实播报配置的模式（人机/热座 + 人数），点击目标不再含糊。
    const s0 = $settings.get();
    localDesc.textContent = trf(s0.mode === 'ai' ? 'qsModeAi' : 'qsModeHotseat', lang, {
      n: s0.playerCount,
    });
    localCard.append(localName, localDesc);
    localCard.addEventListener('click', () => {
      void this.ctx.audio.init();
      // v4.0 修复（SCREENS §14.2）：仅清扫联机残留 mode（联机结束后滞留 'online'
      // 会导致一键开局被误判为联机局）；其余尊重用户配置 —— 此前无条件复位 hotseat
      // 会把用户配置的"人机对弈"踩成 1v1 热座自弈（"自己陪自己玩"）。
      if ($settings.get().mode === 'online') updateSettings({ mode: 'hotseat' });
      this.ctx.goTo('game');
    });

    // v4.0（SCREENS §14.1）：第三主入口——组队对战（2v2/3v3，人+AI 自由搭配）。
    // 三卡色谱：琥珀（本地）→ 金（组队）→ 青（联机）。
    const teamCard = document.createElement('button');
    teamCard.type = 'button';
    teamCard.className = 'menu-item menu-item--team';
    const teamName = document.createElement('span');
    teamName.className = 'mi-name';
    teamName.textContent = t('teamEntry');
    const teamDesc = document.createElement('span');
    teamDesc.className = 'mi-desc';
    teamDesc.textContent = t('teamEntrySub');
    teamCard.append(teamName, teamDesc);
    teamCard.addEventListener('click', () => {
      void this.ctx.audio.init();
      this.openTeamBattle();
    });

    const netCard = document.createElement('button');
    netCard.type = 'button';
    netCard.className = 'menu-item menu-item--net';
    const netName = document.createElement('span');
    netName.className = 'mi-name';
    netName.textContent = t('netMatch');
    const netDesc = document.createElement('span');
    netDesc.className = 'mi-desc';
    netDesc.textContent = t('netMatchDesc');
    netCard.append(netName, netDesc);
    netCard.addEventListener('click', () => this.openOnline());

    menu.append(localCard, teamCard, netCard);

    const linkRow = document.createElement('div');
    linkRow.className = 'menu-links';
    const custom = document.createElement('button');
    custom.type = 'button';
    custom.className = 'mini-btn';
    custom.textContent = t('custom');
    custom.addEventListener('click', () => {
      void this.ctx.audio.init();
      this.ctx.openSettings();
    });
    // v2.1（SCREENS §13.1）：纯邀请分享链接（Web Share 优先，退回渠道面板）。
    const shareLink = document.createElement('button');
    shareLink.type = 'button';
    shareLink.className = 'mini-btn';
    shareLink.textContent = t('share');
    shareLink.addEventListener('click', () => {
      void this.ctx.audio.init();
      share(plainInvite(), this.ctx.overlay, (msg) => this.ctx.hud.toast(msg));
    });
    linkRow.append(custom, shareLink);

    // 版本号（单一数据源 package.json → vite define → __APP_VERSION__）。
    const ver = document.createElement('div');
    ver.className = 'app-version';
    ver.textContent = `v${APP_VERSION}`;

    card.append(ornament, title, latin, sub, rule, tagline, menu, linkRow, ver);

    // 入场编舞：--d 为各层延迟（总时长 ≤900ms，含 0.45s 动画本体）。
    const stagger: [HTMLElement, number][] = [
      [ornament, 0],
      [title, 120],
      [latin, 200],
      [sub, 280],
      [rule, 320],
      [tagline, 320],
      [menu, 440],
      [linkRow, 520],
      [ver, 560],
    ];
    for (const [el, d] of stagger) {
      el.classList.add('stagger-in');
      el.style.setProperty('--d', `${d}ms`);
    }

    root.append(scrim, card);
    this.ctx.overlay.appendChild(root);
  }

  // ── 组队对战（v4.0 · SCREENS §14.1/§15） ──
  /**
   * 打开组队配置面板；开始即把冻结快照写入 settings（mode:'team' + teamMatch）
   * 并开局——与其他模式共用 §4 的"快照冻结于构建时"契约。
   */
  private openTeamBattle(): void {
    openTeamSetup(this.ctx.overlay, (cfg) => {
      netLogger.info('组队对局开始', {
        event: 'match.team.start',
        format: cfg.format,
        humans: cfg.seats.filter((s) => s.kind === 'human').length,
      });
      updateSettings({ mode: 'team', teamMatch: cfg });
      this.ctx.goTo('game');
    });
  }

  // ── 联机面板 ───────────────────────────────────────────
  /**
   * 打开联机面板：幂等建连 + modal 状态机视图。
   * 面板只是 $net 的投影——会话状态全部由 netClient 写入 store，
   * 关闭面板不中断排队 / 对局（服务端持有会话，重开面板即恢复视图）。
   */
  private openOnline(): void {
    void this.ctx.audio.init();
    netLogger.info('联机面板打开', { event: 'net.panel.open' });
    netClient.connect();
    if (this.netVeil) return;

    const veil = document.createElement('div');
    veil.className = 'modal-veil';
    const modal = document.createElement('div');
    modal.className = 'modal net-panel';
    veil.appendChild(modal);
    veil.addEventListener('click', (e) => {
      if (e.target === veil) this.closeOnline();
    });
    this.ctx.overlay.appendChild(veil);
    this.netVeil = veil;
    this.netBodySig = '';

    // 状态变更 → 重渲染；服务端 ERROR 码 → i18n 映射 toast 后消费（防重复提示）。
    this.netPanelUnsub = $net.subscribe((s) => {
      if (s.error) {
        this.ctx.hud.toast(tr(netErrorKey(s.error), $settings.get().language));
        patchNet({ error: null });
      }
      this.renderNetPanel();
    });
    // 排队时长 / 开局倒计时等秒级字段的就地刷新（不触发主体重渲染）。
    this.netTick = setInterval(() => this.tickNetPanel(), 1000);
  }

  private closeOnline(): void {
    if (this.netPanelUnsub) {
      this.netPanelUnsub();
      this.netPanelUnsub = null;
    }
    if (this.netTick !== null) {
      clearInterval(this.netTick);
      this.netTick = null;
    }
    this.netVeil?.remove();
    this.netVeil = null;
  }

  /** 面板骨架（标题 + RTT 角标 + 主体 + 关闭）+ 按会话指纹重渲染主体。 */
  private renderNetPanel(): void {
    const modal = this.netVeil?.querySelector('.modal') as HTMLElement | null;
    if (!modal) return;
    const s = $net.get();
    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);

    let head = modal.querySelector('.net-head') as HTMLElement | null;
    if (!head) {
      head = document.createElement('div');
      head.className = 'net-head';
      const h = document.createElement('h2');
      h.textContent = t('netTitle');
      const rtt = document.createElement('span');
      rtt.className = 'net-rtt';
      head.append(h, rtt);
      const body = document.createElement('div');
      body.className = 'net-body';
      const foot = document.createElement('div');
      foot.className = 'modal-foot';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'btn';
      close.textContent = t('netClose');
      close.addEventListener('click', () => this.closeOnline());
      foot.appendChild(close);
      modal.append(head, body, foot);
    }
    // RTT 角标就地更新（PONG 每 30s 一次，不动主体）。
    const rttEl = head.querySelector('.net-rtt') as HTMLElement;
    rttEl.textContent = s.rttMs === null ? '' : `${t('netRtt')} ${s.rttMs}ms`;

    const sig = JSON.stringify([
      s.status,
      s.party,
      s.playerCount,
      s.queuedAt,
      s.nickname,
      s.playerId,
      s.gameOver ? 1 : 0,
      s.match ? [s.match.matchId, s.match.yourSeat, s.match.startsAt, s.match.players] : null,
    ]);
    if (sig === this.netBodySig) return;
    this.netBodySig = sig;

    const body = modal.querySelector('.net-body') as HTMLElement;
    body.innerHTML = '';
    switch (s.status) {
      case 'offline':
      case 'connecting':
        this.netNote(body, t('netConnecting'));
        break;
      case 'error': {
        this.netNote(body, t('netErrGeneric'));
        const act = document.createElement('div');
        act.className = 'btn-row net-actions';
        this.netButton(act, t('netRetry'), () => netClient.connect(), true);
        body.appendChild(act);
        break;
      }
      case 'idle':
        this.renderNetIdle(body, s);
        break;
      case 'party':
        this.renderNetParty(body, s);
        break;
      case 'in-queue':
        this.renderNetQueue(body, s);
        break;
      case 'matched':
        this.renderNetMatched(body, s);
        break;
      case 'in-match':
        this.renderNetInMatch(body, s);
        break;
    }
    this.tickNetPanel(); // 时间类字段立即填充，不等下一拍
  }

  /** idle 大厅：昵称 / 人数模式 / 快速匹配 / 派对组队。 */
  private renderNetIdle(body: HTMLElement, s: NetState): void {
    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);

    // 昵称（失焦 / 回车生效；服务端 HELLO 时二次消毒）。
    const nickSec = this.netSection(body, t('netNickname'));
    const nickRow = document.createElement('div');
    nickRow.className = 'row';
    const nick = document.createElement('input');
    nick.className = 'net-input';
    nick.type = 'text';
    nick.maxLength = NICKNAME_MAX_LEN;
    nick.placeholder = t('netNicknamePh');
    nick.value = s.nickname;
    nick.addEventListener('change', () => netClient.setNickname(nick.value));
    nick.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') nick.blur();
    });
    nickRow.appendChild(nick);
    nickSec.appendChild(nickRow);

    // 人数模式（2/3/6 —— 联机无 4 人，MATCHMAKING.md §3）。
    const modeSec = this.netSection(body, t('netMode'));
    const modeRow = document.createElement('div');
    modeRow.className = 'row';
    const seg = document.createElement('div');
    seg.className = 'seg';
    for (const n of ONLINE_PLAYER_COUNTS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `seg-btn${n === s.playerCount ? ' on' : ''}`;
      b.textContent = String(n);
      b.addEventListener('click', () => setNetPlayerCount(n));
      seg.appendChild(b);
    }
    modeRow.appendChild(seg);
    modeSec.appendChild(modeRow);

    // 快速匹配（单人入队；派对中由队长在 party 视图发起）。
    const act = document.createElement('div');
    act.className = 'btn-row net-actions';
    this.netButton(act, t('netQuickMatch'), () => netClient.queueJoin(s.playerCount), true);
    body.appendChild(act);

    // 派对：创建 / 凭 3 位邀请码加入（v2.1）。
    const partySec = this.netSection(body, t('netParty'));
    const createRow = document.createElement('div');
    createRow.className = 'row';
    this.netButton(createRow, t('netPartyCreate'), () => netClient.createParty());
    partySec.appendChild(createRow);
    const joinRow = document.createElement('div');
    joinRow.className = 'row';
    const code = document.createElement('input');
    code.className = 'net-input net-code-input';
    code.type = 'text';
    code.maxLength = PARTY_CODE_LENGTH;
    code.placeholder = t('netPartyCodePh');
    const doJoin = (): void => {
      if (code.value.trim()) netClient.joinParty(code.value);
    };
    code.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doJoin();
    });
    this.netButton(joinRow, t('netPartyJoin'), doJoin);
    joinRow.insertBefore(code, joinRow.firstChild);
    partySec.appendChild(joinRow);
    this.netNote(partySec, t('netPartyShare'));
  }

  /** party 房间：邀请码分享 + 成员列表 + 队长发起匹配。 */
  private renderNetParty(body: HTMLElement, s: NetState): void {
    const party = s.party;
    if (!party) return;
    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);
    const isLeader = s.playerId !== null && party.leaderId === s.playerId;

    const codeSec = this.netSection(body, t('netParty'));
    const codeRow = document.createElement('div');
    codeRow.className = 'row';
    const codeEl = document.createElement('span');
    codeEl.className = 'net-party-code';
    codeEl.textContent = party.code;
    codeRow.appendChild(codeEl);
    this.netButton(codeRow, t('dbgCopy'), () => {
      void navigator.clipboard
        ?.writeText(party.code)
        ?.then(() => this.ctx.hud.toast(tr('netCopied', $settings.get().language)))
        .catch(() => undefined);
    });
    // v2.1（SCREENS §13.1）：邀请码分享（同一管线，文案含 3 位组队码）。
    this.netButton(codeRow, t('share'), () => {
      share(partyInvite(party.code), this.ctx.overlay, (msg) => this.ctx.hud.toast(msg));
    });
    codeSec.appendChild(codeRow);

    // 成员列表（断线成员置灰，座位为其保留至宽限期结束）。
    const list = document.createElement('div');
    list.className = 'net-members';
    for (const m of party.members) {
      const row = document.createElement('div');
      row.className = `player-row net-member${m.connected ? '' : ' off'}`;
      const dot = document.createElement('span');
      dot.className = 'dot';
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = m.playerId === s.playerId ? `${m.nickname}（${t('you')}）` : m.nickname;
      const tag = document.createElement('span');
      tag.className = 'count';
      tag.textContent = m.playerId === party.leaderId ? t('netPartyLeader') : '';
      row.append(dot, name, tag);
      list.appendChild(row);
    }
    codeSec.appendChild(list);

    const act = document.createElement('div');
    act.className = 'btn-row net-actions';
    if (isLeader) {
      // 队长：沿用大厅的人数模式选择，整队入队。
      this.netButton(act, t('netQuickMatch'), () => netClient.queueJoin(s.playerCount), true);
    } else {
      this.netNote(act, t('netPartyWaitLeader'));
    }
    this.netButton(act, t('netPartyLeave'), () => netClient.leaveParty());
    body.appendChild(act);
  }

  /** in-queue：匹配等待（秒级计时 + 软性提示 + 取消）。 */
  private renderNetQueue(body: HTMLElement, s: NetState): void {
    const lang = $settings.get().language;
    this.netNote(body, `${tr('netMatching', lang)} · ${tr('players', lang)} ${s.playerCount}`);
    const timer = document.createElement('div');
    timer.className = 'net-queue-timer';
    timer.dataset.net = 'elapsed';
    body.appendChild(timer);
    // v3.1：服务端 QUEUE_STATUS 驱动的对手缺口 + AI 补位倒计时（就地刷新，不进 sig）。
    const deficit = document.createElement('div');
    deficit.className = 'net-tip net-queue-deficit';
    deficit.dataset.net = 'deficit';
    body.appendChild(deficit);
    const aiFill = document.createElement('div');
    aiFill.className = 'net-tip net-queue-aifill';
    aiFill.dataset.net = 'aifill';
    body.appendChild(aiFill);
    const tip = document.createElement('div');
    tip.className = 'net-tip';
    tip.dataset.net = 'tip';
    body.appendChild(tip);
    const act = document.createElement('div');
    act.className = 'btn-row net-actions';
    this.netButton(act, tr('netCancelMatch', lang), () => netClient.queueCancel(), true);
    body.appendChild(act);
  }

  /** matched：开局倒计时 + 对阵名册（座位色 + 昵称）。 */
  private renderNetMatched(body: HTMLElement, s: NetState): void {
    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);
    this.netNote(body, t('netMatched'));
    const count = document.createElement('div');
    count.className = 'net-countdown';
    count.dataset.net = 'countdown';
    body.appendChild(count);
    if (!s.match) return;
    const list = document.createElement('div');
    list.className = 'net-members';
    for (const p of s.match.players) {
      const row = document.createElement('div');
      row.className = 'player-row net-member';
      const dot = document.createElement('span');
      dot.className = 'dot';
      dot.style.color = PLAYER_COLORS[p.seat] ?? '#E63946';
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent =
        (p.playerId === s.playerId ? `${p.nickname}（${t('you')}）` : p.nickname) +
        (p.isAi ? ` · ${t('ai')}` : '');
      row.append(dot, name);
      list.appendChild(row);
    }
    body.appendChild(list);
  }

  /** in-match：引导返回对局（rematch 窗口期交给 ResultScene / 自动导航）。 */
  private renderNetInMatch(body: HTMLElement, s: NetState): void {
    const lang = $settings.get().language;
    this.netNote(body, tr('netInMatchTip', lang));
    if (s.gameOver) {
      this.netNote(body, tr('netRematchWait', lang));
      return;
    }
    const act = document.createElement('div');
    act.className = 'btn-row net-actions';
    this.netButton(
      act,
      tr('netReturnMatch', lang),
      () => {
        this.closeOnline();
        this.ctx.goTo('game');
      },
      true
    );
    body.appendChild(act);
  }

  /** 秒级字段就地刷新：排队时长（服务端时钟换算）、软性提示、开局倒计时。 */
  private tickNetPanel(): void {
    const modal = this.netVeil?.querySelector('.modal');
    if (!modal) return;
    const s = $net.get();
    const lang = $settings.get().language;

    const elapsedEl = modal.querySelector('[data-net="elapsed"]') as HTMLElement | null;
    if (elapsedEl) {
      // queuedAt 是服务端时间戳：本地此刻的服务端时钟 ≈ Date.now() + clockOffset。
      const ms = s.queuedAt === null ? 0 : Math.max(0, Date.now() + s.clockOffset - s.queuedAt);
      const mm = Math.floor(ms / 60_000);
      const ss = Math.floor((ms % 60_000) / 1000);
      elapsedEl.textContent = `${tr('netQueuedFor', lang)} ${mm}:${String(ss).padStart(2, '0')}`;
      const tipEl = modal.querySelector('[data-net="tip"]') as HTMLElement | null;
      if (tipEl) {
        tipEl.textContent =
          ms >= SLOW_TIP_MS
            ? tr('netSlowTip', lang)
            : ms >= SOFT_TIP_MS
              ? tr('netSoftTip', lang)
              : '';
      }
    }

    // 队列进度：deficit 直接渲染；fillAiInMs 以 receivedAt 为锚点本地自减（帧仅校时）。
    const qs = s.queueStatus;
    const deficitEl = modal.querySelector('[data-net="deficit"]') as HTMLElement | null;
    if (deficitEl) {
      deficitEl.textContent =
        qs && qs.deficit > 0 ? trf('netQueueDeficit', lang, { n: qs.deficit }) : '';
    }
    const aiFillEl = modal.querySelector('[data-net="aifill"]') as HTMLElement | null;
    if (aiFillEl) {
      if (qs && qs.deficit > 0) {
        const leftMs = Math.max(0, qs.fillAiInMs - (Date.now() - qs.receivedAt));
        aiFillEl.textContent = trf('netQueueFillAi', lang, { s: Math.ceil(leftMs / 1000) });
      } else {
        aiFillEl.textContent = '';
      }
    }

    const countEl = modal.querySelector('[data-net="countdown"]') as HTMLElement | null;
    if (countEl && s.match) {
      // startsAt 已是本地时钟（MATCH_FOUND 时换算）。
      countEl.textContent = String(Math.max(0, Math.ceil((s.match.startsAt - Date.now()) / 1000)));
    }
  }

  // ── 联机面板小构件 ──────────────────────────────────────
  private netSection(body: HTMLElement, title: string): HTMLElement {
    const sec = document.createElement('div');
    sec.className = 'section';
    const h = document.createElement('div');
    h.className = 'sec-title';
    h.textContent = title;
    sec.appendChild(h);
    body.appendChild(sec);
    return sec;
  }

  private netNote(body: HTMLElement, text: string): void {
    const note = document.createElement('div');
    note.className = 'modal-sub';
    note.textContent = text;
    body.appendChild(note);
  }

  private netButton(
    parent: HTMLElement,
    label: string,
    onClick: () => void,
    primary = false
  ): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = primary ? 'btn btn-primary' : 'mini-btn';
    b.textContent = label;
    b.addEventListener('click', onClick);
    parent.appendChild(b);
    return b;
  }

  // ── Live settings ───────────────────────────────────────
  private bindSettings(): void {
    // 主题变更由 main 统一走 onThemeChange → buildStage，这里只跟随虹彩参数。
    this.unsubs.push(
      $settings.subscribe((s) => {
        for (const m of this.marbles) m.material.iridescence = s.marbleIridescence;
      })
    );
  }
}
